import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  PromptSortOptions,
  PromptTableSortOptions,
  PromptView,
  SortDirection,
} from '../../Types/enums';
import { Prompt, PromptAPIResponse } from '../../Types/prompt';
import { PromptLabelCounts } from '../../Types/tags';
import {
  findFeaturedPrompts,
  findPrompts,
  findTopPromptsLast30Days,
  getPromptsIncrementally,
} from '../actions/promptActions';
import { PROMPT_PAGE_SIZE } from '../consts';
import { RootState } from '../store';

interface PromptsState {
  prompts: Prompt[];
  featuredPrompts: Prompt[];
  topPrompts: Prompt[];
  currentPrompt?: Prompt;
  totalCount: number;
  isLoading: boolean;

  publishedOnly?: boolean;
  myPromptsOnly?: boolean;
  searchQuery: string;
  page: number;
  pageOffset: number;
  limit: number;
  totalPages: number;
  sortOption: PromptSortOptions | PromptTableSortOptions;
  sortDirection: SortDirection;
  labelOptions: PromptLabelCounts;
  selectedLabels: string[];
  excludedLabels: string[];
  featuredOnly: boolean;
  recentOnly: boolean;
  pinned?: boolean;
  folderPinned?: boolean;
  liked?: boolean;
  archivedOnly?: boolean;
  promptView: PromptView;
  latestFindPromptsRequestId?: string;
}

const initialState: PromptsState = {
  prompts: [],
  featuredPrompts: [],
  topPrompts: [],
  currentPrompt: undefined,
  totalCount: 0,
  isLoading: false,

  publishedOnly: true,
  myPromptsOnly: false,
  searchQuery: '',
  page: 1,
  pageOffset: 0,
  limit: PROMPT_PAGE_SIZE,
  totalPages: 0,
  sortOption: PromptSortOptions.DEFAULT,
  sortDirection: SortDirection.DESC,
  labelOptions: [],
  selectedLabels: [],
  excludedLabels: [],
  featuredOnly: false,
  recentOnly: false,
  archivedOnly: false,
  promptView: PromptView.ALL_PROMPTS,
  latestFindPromptsRequestId: undefined,
};

const promptsSlice = createSlice({
  name: 'prompts',
  initialState,
  reducers: {
    setCurrentPrompt: (state, action: PayloadAction<Prompt | undefined>) => {
      state.currentPrompt = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
      const safePage = Math.max(1, action.payload);
      state.pageOffset = (safePage - 1) * PROMPT_PAGE_SIZE;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload;
    },
    setPromptView: (state, action: PayloadAction<PromptView>) => {
      state.promptView = action.payload;
      state.page = 1;
      state.pageOffset = 0;
    },
    setPublishedOnly: (state, action: PayloadAction<boolean | undefined>) => {
      state.publishedOnly = action.payload;
      state.selectedLabels = [];
      state.page = 1;
      state.pageOffset = 0;
    },
    setMyPromptsOnly: (state, action: PayloadAction<boolean | undefined>) => {
      state.myPromptsOnly = action.payload;
      state.page = 1;
      state.pageOffset = 0;
    },
    setSortOption: (state, action: PayloadAction<PromptSortOptions | PromptTableSortOptions>) => {
      state.sortOption = action.payload;
      state.page = 1;
      state.pageOffset = 0;
    },
    setSortDirection: (state, action: PayloadAction<SortDirection>) => {
      state.sortDirection = action.payload;
      state.page = 1;
      state.pageOffset = 0;
    },
    setLabelOptions: (state, action: PayloadAction<PromptLabelCounts>) => {
      state.labelOptions = action.payload;
    },
    setSelectedLabels: (state, action: PayloadAction<string[]>) => {
      state.selectedLabels = action.payload;
      state.page = 1;
      state.pageOffset = 0;
    },
    setExcludedLabels: (state, action: PayloadAction<string[]>) => {
      state.excludedLabels = action.payload;
    },
    setFeaturedOnly: (state, action: PayloadAction<boolean>) => {
      state.featuredOnly = action.payload;
    },
    setRecent: (state, action: PayloadAction<boolean>) => {
      state.recentOnly = action.payload;
    },
    setPinned: (state, action: PayloadAction<boolean | undefined>) => {
      state.pinned = action.payload;
    },
    setFolderPinned: (state, action: PayloadAction<boolean | undefined>) => {
      state.folderPinned = action.payload;
    },
    setLiked: (state, action: PayloadAction<boolean | undefined>) => {
      state.liked = action.payload;
    },
    setArchivedOnly: (state, action: PayloadAction<boolean | undefined>) => {
      state.archivedOnly = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(findPrompts.pending, (state, action) => {
        state.latestFindPromptsRequestId = action.meta.requestId;
      })
      .addCase(findPrompts.fulfilled, (state, action) => {
        // Ignore stale responses to avoid race conditions when the user
        // quickly switches tabs/filters and an older request resolves last.
        if (action.meta.requestId !== state.latestFindPromptsRequestId) {
          return;
        }

        const { data, totalCount } = action.payload;

        state.prompts = data;
        state.totalCount = totalCount;
        state.totalPages = Math.ceil(totalCount / PROMPT_PAGE_SIZE);
        state.isLoading = false;
      })
      .addCase(findFeaturedPrompts.fulfilled, (state, action: PayloadAction<Prompt[]>) => {
        state.featuredPrompts = action.payload;
      })
      .addCase(findTopPromptsLast30Days.fulfilled, (state, action: PayloadAction<Prompt[]>) => {
        state.topPrompts = action.payload;
      })
      .addCase(
        getPromptsIncrementally.fulfilled,
        (state, action: PayloadAction<PromptAPIResponse>) => {
          const { data, totalCount } = action.payload;
          const pageOffset = state.pageOffset;

          if (pageOffset === 0) {
            state.prompts = data;
          } else {
            state.prompts = [...state.prompts, ...data];
          }

          state.totalCount = totalCount;
          state.totalPages = Math.ceil(totalCount / PROMPT_PAGE_SIZE);
          state.isLoading = false;
        }
      )
      .addMatcher(
        (action: PayloadAction) =>
          action.type.startsWith('prompts/') && action.type.endsWith('/rejected'),
        (state) => {
          state.isLoading = false;
        }
      );
  },
});

export const {
  setCurrentPrompt,
  setIsLoading,
  setPage,
  setLimit,
  setPromptView,
  setSortDirection,
  setSortOption,
  setSearchQuery,
  setSelectedLabels,
  setExcludedLabels,
  setPublishedOnly,
  setMyPromptsOnly,
  setRecent,
  setPinned,
  setFolderPinned,
  setLabelOptions,
  setArchivedOnly,
} = promptsSlice.actions;

export const selectPrompts = (state: RootState) => state.prompts;

export default promptsSlice.reducer;
